import { useEffect, useMemo, useState } from "react";
import { ArrowDownToLine, ArrowLeftRight, ArrowUpFromLine, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import type { InventoryRow, MovementType, WarehouseCode } from "../types";
import { applyMovement, enableMalhariaStock, transferStock } from "../services/inventory";
import { Modal } from "./Modal";

type Action = "entrada" | "saida" | "ajuste" | "transferencia";

export function QuickMovement({ open, onClose, rows, initialVariantId }: { open: boolean; onClose: () => void; rows: InventoryRow[]; initialVariantId?: string }) {
  const [variantId, setVariantId] = useState("");
  const [action, setAction] = useState<Action>("entrada");
  const [warehouse, setWarehouse] = useState<WarehouseCode>("galpao");
  const [quantity, setQuantity] = useState(1);
  const [setTo, setSetTo] = useState(0);
  const [note, setNote] = useState("");
  const [activateMalharia, setActivateMalharia] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setVariantId(initialVariantId ?? rows[0]?.variant.id ?? "");
      setWarehouse("galpao");
      setActivateMalharia(false);
    }
  }, [open, initialVariantId, rows]);
  const selected = useMemo(() => rows.find((row) => row.variant.id === variantId), [rows, variantId]);
  const malhariaAvailable = Boolean(selected?.variant.malhariaEnabled || activateMalharia);
  useEffect(() => {
    setActivateMalharia(false);
    if (!selected?.variant.malhariaEnabled) setWarehouse("galpao");
  }, [variantId, selected?.variant.malhariaEnabled]);
  useEffect(() => {
    const balance = warehouse === "galpao" ? selected?.galpao : selected?.malharia;
    setSetTo(balance?.quantity ?? 0);
  }, [selected, warehouse]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!variantId) return;
    setSaving(true);
    try {
      if (activateMalharia && !selected?.variant.malhariaEnabled) await enableMalhariaStock(variantId);
      if (action === "transferencia") {
        await transferStock(variantId, warehouse, quantity, note);
      } else {
        const type: MovementType = action === "entrada" ? "entrada" : action === "saida" ? "venda" : "alteracao_manual";
        await applyMovement({ variantId, warehouse, type, quantity: action === "saida" ? -quantity : quantity, setTo: action === "ajuste" ? setTo : undefined, note });
      }
      toast.success(action === "transferencia" ? "Transferência registrada sem alterar o estoque total." : "Movimentação registrada no histórico.");
      setNote("");
      onClose();
    } catch (error) { toast.error(error instanceof Error ? error.message : "Não foi possível registrar a movimentação."); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Movimentação rápida" description="Toda alteração gera histórico e entra na fila de sincronização.">
      <form onSubmit={submit} className="form-stack">
        <label><span>Produto / SKU / Cor</span><select value={variantId} onChange={(e) => setVariantId(e.target.value)} required>{rows.map((row) => <option key={row.variant.id} value={row.variant.id}>{row.product.sku} · {row.product.name} · {row.variant.colorName}</option>)}</select></label>
        <div className="segmented movement-types">
          <button type="button" className={action === "entrada" ? "active" : ""} onClick={() => setAction("entrada")}><ArrowDownToLine size={18} />Entrada</button>
          <button type="button" className={action === "saida" ? "active" : ""} onClick={() => setAction("saida")}><ArrowUpFromLine size={18} />Saída</button>
          <button type="button" className={action === "ajuste" ? "active" : ""} onClick={() => setAction("ajuste")}><SlidersHorizontal size={18} />Ajuste</button>
          <button type="button" className={action === "transferencia" ? "active" : ""} onClick={() => setAction("transferencia")} disabled={!malhariaAvailable}><ArrowLeftRight size={18} />Transferir</button>
        </div>
        <div className="stock-context">
          <div><span>Galpão</span><strong>{selected?.galpao.quantity ?? 0}</strong></div>
          <div className={!malhariaAvailable ? "disabled" : ""}><span>Malharia</span><strong>{malhariaAvailable ? selected?.malharia?.quantity ?? 0 : "—"}</strong></div>
          <div><span>Total</span><strong>{selected?.total ?? 0}</strong></div>
        </div>
        {!selected?.variant.malhariaEnabled && <label className="malharia-activation"><input type="checkbox" checked={activateMalharia} onChange={(event) => { setActivateMalharia(event.target.checked); if (!event.target.checked && warehouse === "malharia") setWarehouse("galpao"); }} /><span><strong>Ativar estoque da malharia</strong><small>Cria o saldo da Malharia para esta cor e permite entradas, ajustes e transferências.</small></span></label>}
        <div className="form-grid two">
          <label><span>{action === "transferencia" ? "Depósito de origem" : "Depósito"}</span><select value={warehouse} onChange={(e) => setWarehouse(e.target.value as WarehouseCode)}><option value="galpao">Galpão</option>{malhariaAvailable && <option value="malharia">Malharia</option>}</select></label>
          {action === "ajuste" ? <label><span>Ajustar estoque para</span><input type="number" value={setTo} onChange={(e) => setSetTo(Number(e.target.value))} /></label> : <label><span>Quantidade</span><input type="number" min="1" value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} required /></label>}
        </div>
        <label><span>Motivo / observação</span><textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={action === "ajuste" ? "Inventário, divergência, perda, defeito…" : "Opcional"} /></label>
        <div className="modal-actions"><button type="button" className="ghost-button" onClick={onClose}>Cancelar</button><button className="primary-button" disabled={saving || !variantId}>{saving ? "Registrando…" : "Confirmar movimentação"}</button></div>
      </form>
    </Modal>
  );
}
